import GitHubIcon from '@mui/icons-material/GitHub';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  getSurfaceBorder,
  getSurfaceColor,
  radii,
} from '../../../theme/tokens';
import { EntityPanelFrame } from './EntityPanelFrame';

type TeamLinkProps = {
  href: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
};

const TeamLink = ({ href, icon, title, subtitle }: TeamLinkProps) => {
  const theme = useTheme();

  return (
    <Link
      href={href}
      target="_blank"
      rel="noreferrer"
      underline="none"
      color="inherit"
      sx={{
        alignItems: 'center',
        backgroundColor: getSurfaceColor(theme, 'subtle'),
        border: `1px solid ${getSurfaceBorder(theme)}`,
        borderRadius: `${radii.md}px`,
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.5,
        transition: 'background-color 150ms ease, border-color 150ms ease',
        '&:hover': {
          backgroundColor: getSurfaceColor(theme, 'raised'),
          borderColor: getSurfaceBorder(theme, 'strong'),
        },
      }}
    >
      {icon}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ lineHeight: 1.35 }}>
          {title}
        </Typography>
        <Typography
          color="text.secondary"
          variant="caption"
          sx={{ lineHeight: 1.3 }}
        >
          {subtitle}
        </Typography>
      </Box>
      <LaunchRoundedIcon
        sx={{ color: 'text.disabled', fontSize: 18, flexShrink: 0 }}
      />
    </Link>
  );
};

export const EntityTeamLinksCard = () => {
  const { entity } = useEntity();

  const githubSlug = entity.metadata.annotations?.['github.com/team-slug'];

  if (!githubSlug) return null;

  return (
    <EntityPanelFrame title="Links">
      <Stack spacing={1.5}>
        <TeamLink
          href={`https://github.com/orgs/meltwater/teams/${githubSlug}/repositories`}
          icon={<GitHubIcon sx={{ color: 'text.secondary', fontSize: 20 }} />}
          title="GitHub repositories"
          subtitle={`meltwater/${githubSlug}`}
        />
      </Stack>
    </EntityPanelFrame>
  );
};
